<template>
  <VSpinner v-if="!product" class="offset-top-16 offset-bottom-17" />
  <article v-else>
    <VForm :form="form" @submit="btnNext">
      <div id="PRODUCT_ADMINISTRATION_SELECTION" class="container-fixed">
        <VProgress v-if="flags.progressBarEnabled" class="offset-top-2 offset-bottom-2" :steps="steps" active="substore.progress_selection" />

        <section v-if="product.texts.productName && product.texts.providerName">
          <div class="row">
            <div class="col-l-6">
              <h3 class="h1" v-html="product.texts.productName"></h3>
              <p>
                <strong>
                  {{ $t('substore.provider') }}:
                  <span v-html="product.texts.providerName"></span>
                </strong>
              </p>
            </div>
          </div>
          <hr />
        </section>

        <section v-if="product.texts.descriptionAdministrationSelection">
          <p v-html="product.texts.descriptionAdministrationSelection"></p>
        </section>

        <VInput v-model="modifiedFixedLineNumber" name="fln" :label="labelFLN" :hint="$t('substore.Web2T_NotValidFixedLineNumber')" :placeholder="textFLN" required/>

        <section v-if="product.texts.hintDescriptionAdministrationSelection">
          <p v-html="product.texts.hintDescriptionAdministrationSelection"></p>
        </section>
      </div>

      <div class="container-fixed">
        <div class="row">
          <section class="offset-top-2 pull-right offset-right-1 button-bar">
            <button type="button" class="btn btn-clean" @click="btnAbort">
              {{ $t('substore.abort') }}
            </button>
            <button type="submit" class="offset-left-1 btn btn-default btn-brand">
              {{ $t('substore.next') }}
            </button>
          </section>
        </div>
      </div>
    </VForm>

    <div class="container-fixed offset-top-6">
      <VFootnotes :footnotes="footnotes" />
    </div>
  </article>
</template>

<script>
import { mapGetters } from 'vuex';
import Form from '@/components/Form/Form';
import Input from '@/components/Form/Input';
import { checkSupportedFixedLineNumbers, formattedFixedLineNumber, initFields, isValidFixedLineNumber } from '@/utils/form';
import AdministrationProxy from '@/proxies/SubstoreAdministrationProxy';
import Footnotes from '@/utils/Footnotes';
import TitleMixin from '@/mixins/title';
import OrderStepsMixin from '@/mixins/administrationSteps';
import VSpinner from '@/components/Spinner';
import Transformer from '@/transformers/ProductAdministrationSelectionTransformer';
import { HOME, PRODUCT_ADMINISTRATION_SUMMARY, PRODUCT_CATALOGUE, STOCK } from '@route-types';

export default {
  name: 'ProductAdministrationSelection',

  components: {
    VSpinner,
    VProgress: () => import('@/components/Progress'),
    VForm: Form,
    VInput: Input,
    VFootnotes: () => import('@/components/UI/Footnotes'),
  },

  mixins: [TitleMixin, OrderStepsMixin],

  props: {
    subscriptionTemplateId: {
      type: String,
      required: true,
    },

    uniqueSubscriptionId: {
      type: String,
      required: true,
    },

    fixedLineNumber: {
      type: String,
      required: false,
      default: null,
    },
  },

  data() {
    return {
      modifiedFixedLineNumber: this.fixedLineNumber,
      product: null,
      footnotes: null,
      form: initFields({
        name: 'fln',
        validators: [isValidFixedLineNumber, checkSupportedFixedLineNumbers],
        filters: [formattedFixedLineNumber],
        isHidden: true,
      }),
      flags: {
        progressBarEnabled: null,
        currentParameterValue: null,
      },
      texts: [],
      optin: false,
    };
  },

  computed: {
    ...mapGetters('system', ['hasAccessCatalogue', 'hasAccessStockOverview']),

    labelFLN() {
      if (this.product.texts.fixedLineNumberLabel) {
        return this.product.texts.fixedLineNumberLabel;
      }
      return this.$t('substore.Web2T_FixedLineNumberLabel');
    },

    textFLN() {
      if (this.product.texts.fixedLineNumberText) {
        return this.product.texts.fixedLineNumberText;
      }
      return this.$t('substore.Web2T_FixedLineNumberText');
    },
  },

  async created() {
    const response = await new AdministrationProxy().orderAdministrationDialogData(this.subscriptionTemplateId, this.uniqueSubscriptionId);

    Object.keys(this.flags).forEach(key => {
      this.flags[key] = response.data[key];
    });

    this.footnotes = new Footnotes();
    this.product = Transformer.fetch(response.data.product, this.footnotes);

    if (this.product.inputType === 'FLN') {
      this.form.fln.isHidden = false;
    }

    this.$webtrekk.viewPageWithPayload('administrationauswahl', {
      page_type: 'basket',
      shop_product_sku: this.subscriptionTemplateId,
      shop_product_name: this.product.texts?.productName,
    });
  },

  methods: {
    btnAbort() {
      if (this.hasAccessStockOverview) {
        this.$router.push({ name: STOCK });
      } else if (this.hasAccessCatalogue) {
        this.$router.push({ name: PRODUCT_CATALOGUE });
      } else {
        this.$router.push({ name: HOME });
      }
    },

    btnNext() {
      this.$router.push({
        name: PRODUCT_ADMINISTRATION_SUMMARY,
        params: {
          subscriptionTemplateId: this.subscriptionTemplateId,
          uniqueSubscriptionId: this.uniqueSubscriptionId,
          requestedParameterValue: this.modifiedFixedLineNumber,
        },
      });
    },
  },
};
</script>

<style scoped>
fieldset legend {
  padding-top: 24px;
}
fieldset {
  padding-bottom: 24px;
}

.button-bar > span {
  position: relative;
  top: 1px;
  display: inline-block;
}
</style>
